import { IonButton, IonIcon, useIonActionSheet, useIonToast } from '@ionic/react';
import { Capacitor } from '@capacitor/core';
import {
  savePendingCropPicture,
  uploadCropPictureOptions,
  UploadPictureDto,
  useUploadCropPicture,
} from '@lidea/shared/data-access/analysis';
import { usePlot } from '@lidea/shared/data-access/plots';
import { isManualOffline } from '@lidea/shared/feature-offline';
import {
  compressImageForUpload,
  isImageDownscaleError,
  markPicturePickerActive,
  markPicturePickerReturned,
  prepareOfflineCropPictureArchive,
  isCapacitorCameraPickerAvailable,
  pickCropPicture,
  saveCropPictureWithUserGesture,
  waitForStableNetworkAfterPicker,
  whenCapacitorCameraPickerAvailable,
} from '@lidea/shared/util/core';
import {
  Collapse,
  FileButton,
  Group,
  Image,
  Loader,
  LoadingOverlay,
  Popover,
  Stack,
  Text,
} from '@mantine/core';
import { Mutation, useMutationState } from '@tanstack/react-query';
import { cameraOutline, imageOutline, information } from 'ionicons/icons';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const root = import.meta.env.VITE_API_ROOT;

interface ImageProcessingLoaderProps {
  message: string | null;
  withLoader?: boolean;
}
const ImageProcessingLoader: React.FC<ImageProcessingLoaderProps> = ({
  message,
  withLoader = true,
}) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontWeight: 900,
      padding: 16,
      textAlign: 'center',
      color: 'black',
      flexWrap: 'nowrap',
      gap: 8,
    }}
  >
    <Popover width={200} withinPortal>
      <Popover.Target>
        <IonButton fill="solid" size="small">
          {withLoader ? <Loader color="white" size="xs" /> : null}
          <IonIcon icon={information} slot="icon-only"></IonIcon>
        </IonButton>
      </Popover.Target>
      <Popover.Dropdown>
        <Text>{message}</Text>
      </Popover.Dropdown>
    </Popover>
  </div>
);

interface PictureInputProps {
  fieldId: string;
  analysisId?: number;
  measureZone: number;
  measureId: number;
  group: string;
  isProcessing: boolean;
  imageUrl?: string;
  hasProcessError?: boolean | null;
}

export const PictureInput: React.FC<PictureInputProps> = ({
  fieldId,
  analysisId,
  measureZone,
  measureId,
  group,
  isProcessing,
  imageUrl,
  hasProcessError,
}) => {
  const { t } = useTranslation(['analysis', 'plot', 'common'], {
    useSuspense: false,
  });
  const [presentCameraSheet] = useIonActionSheet();
  const [presentToast] = useIonToast();
  const [showImage, setShowImage] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isPreparingPicture, setIsPreparingPicture] = useState(false);
  const [isWaitingForNetwork, setIsWaitingForNetwork] = useState(false);
  const resetCameraRef = useRef<() => void>(null);
  const resetGalleryRef = useRef<() => void>(null);
  const systemCameraTriggerRef = useRef<HTMLButtonElement>(null);
  const uploadCropPicture = useUploadCropPicture({
    fieldId,
    measureZone,
    analysisId,
    measureId,
  });
  const plot = usePlot(Number(fieldId));
  const plotName = plot.data?.properties.name ?? '';
  const uploadMutationState = useMutationState({
    filters: {
      mutationKey: uploadCropPictureOptions.mutationKey,
      status: 'pending',
      predicate: (mutation) => {
        const variables = mutation.state.variables as UploadPictureDto;
        return variables?.measureId === measureId && variables?.field === group;
      },
    },
    select: ({ state }) => ({
      state: state as Mutation<unknown, Error, UploadPictureDto>['state'],
    }),
  });

  const [inAppCameraReady, setInAppCameraReady] = useState(
    isCapacitorCameraPickerAvailable()
  );

  useEffect(() => {
    if (inAppCameraReady) {
      return;
    }

    let cancelled = false;
    whenCapacitorCameraPickerAvailable().then((ready) => {
      if (!cancelled && ready) {
        setInAppCameraReady(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [inAppCameraReady]);

  const handlePictureSelected = async (
    file: File | null,
    options: { downscale?: boolean; skipOfflineArchive?: boolean } = {}
  ) => {
    if (!file) {
      return;
    }

    let preparedFile = file;

    // System camera returns full-res files. Downscale during decode (resizeWidth)
    // before IDB/upload. Gallery skips this — decode/canvas is riskier than uploading as-is.
    if (options.downscale) {
      setIsPreparingPicture(true);
      try {
        preparedFile = await compressImageForUpload(file);
      } catch (error) {
        if (isImageDownscaleError(error)) {
          void presentToast({
            message: t('analysis:picture_downscale_failed')!,
            duration: 5000,
            color: 'warning',
          });
        }
        return;
      } finally {
        setIsPreparingPicture(false);
      }
    }

    let archiveFile: File | null = null;

    if (isManualOffline() && !options.skipOfflineArchive) {
      const archiveResult = await prepareOfflineCropPictureArchive(preparedFile, {
        plotName,
        pointNumber: measureZone,
        fieldCode: group,
      });

      if (archiveResult?.needsUserSave) {
        archiveFile = archiveResult.file;
      }
    }

    setIsWaitingForNetwork(true);
    try {
      await waitForStableNetworkAfterPicker();
    } finally {
      setIsWaitingForNetwork(false);
    }

    if (archiveFile) {
      presentCameraSheet({
        header: t('analysis:crop_archive_save_header') ?? undefined,
        subHeader: t('analysis:crop_archive_save_message') ?? undefined,
        buttons: [
          {
            text: t('analysis:crop_archive_save_action')!,
            handler: () => {
              void saveCropPictureWithUserGesture(archiveFile!);
            },
          },
          {
            text: t('common:cancel')!,
            role: 'cancel',
          },
        ],
      });
    }

    // Persist bytes before enqueueing so a kill during HL pause cannot drop the photo.
    const pictureId = await savePendingCropPicture(preparedFile, {
      measureId,
      field: group,
    });

    // Bytes live in IDB only — avoid holding a second File copy in mutation state.
    uploadCropPicture.mutate(
      {
        measureId,
        field: group,
        pictureId,
      },
      {
        onSuccess: () => {
          resetCameraRef.current?.();
          resetGalleryRef.current?.();
        },
      }
    );
  };

  const openSystemCamera = () => {
    systemCameraTriggerRef.current?.click();
  };

  const openInAppCamera = async () => {
    // PWA in-app camera must prompt save inside the overlay (fresh tap for Web Share).
    const archiveInCamera =
      isManualOffline() && !Capacitor.isNativePlatform();
    const archiveMeta = archiveInCamera
      ? {
          plotName,
          pointNumber: measureZone,
          fieldCode: group,
        }
      : undefined;
    const file = await pickCropPicture('camera', { archiveMeta });
    await handlePictureSelected(file, {
      skipOfflineArchive: archiveInCamera,
    });
  };

  const handleCameraButtonClick = () => {
    if (isPending || isProcessing) {
      return;
    }

    if (!inAppCameraReady) {
      openSystemCamera();
      return;
    }

    presentCameraSheet({
      header: t('analysis:camera_choice_header') ?? undefined,
      buttons: [
        {
          text: t('analysis:camera_choice_system')!,
          handler: () => {
            openSystemCamera();
          },
        },
        {
          text: t('analysis:camera_choice_in_app')!,
          handler: () => {
            void openInAppCamera();
          },
        },
        {
          text: t('common:cancel')!,
          role: 'cancel',
        },
      ],
    });
  };

  const handleSystemCameraFile = async (file: File | null) => {
    markPicturePickerReturned();
    await handlePictureSelected(file, { downscale: true });
  };

  const handleGalleryFile = async (file: File | null) => {
    markPicturePickerReturned();
    await handlePictureSelected(file);
  };

  const isPending =
    isPreparingPicture ||
    isWaitingForNetwork ||
    uploadCropPicture.isPending ||
    uploadMutationState.length > 0;

  const isPaused =
    uploadCropPicture.isPaused ||
    uploadMutationState.filter((m) => m.state.isPaused).length > 0;

  return (
    <Stack spacing={4}>
      <div>
        <Group pos="relative" spacing={0}>
          <LoadingOverlay
            loader={
              <ImageProcessingLoader
                message={
                  isPaused
                    ? t('analysis:uploading_picture_paused')
                    : isPreparingPicture
                    ? t('analysis:preparing_picture')
                    : isWaitingForNetwork
                    ? t('analysis:waiting_for_network')
                    : isPending
                    ? t('analysis:uploading_picture')
                    : isProcessing
                    ? t('analysis:processing_picture')
                    : null
                }
                withLoader={!isPaused}
              />
            }
            overlayOpacity={0.9}
            visible={isPending || isProcessing}
          />
          {/* Hidden trigger for the system camera / capture input. */}
          <FileButton
            accept="image/*"
            capture="environment"
            resetRef={resetCameraRef}
            onChange={(e) => handleSystemCameraFile(e)}
          >
            {(props) => (
              <button
                {...props}
                ref={systemCameraTriggerRef}
                aria-hidden="true"
                tabIndex={-1}
                type="button"
                style={{
                  position: 'absolute',
                  width: 1,
                  height: 1,
                  opacity: 0,
                  pointerEvents: 'none',
                  overflow: 'hidden',
                }}
                onClick={(event) => {
                  markPicturePickerActive();
                  props.onClick?.(event);
                }}
              />
            )}
          </FileButton>
          <IonButton
            aria-label={t('analysis:take_crop_picture')!}
            disabled={isPending || isProcessing}
            expand="block"
            style={{ flexGrow: 1 }}
            onClick={handleCameraButtonClick}
          >
            <IonIcon
              aria-hidden="true"
              icon={cameraOutline}
              slot="icon-only"
            ></IonIcon>
          </IonButton>
          <FileButton
            accept="image/*"
            resetRef={resetGalleryRef}
            onChange={(e) => handleGalleryFile(e)}
          >
            {(props) => (
              <IonButton
                aria-label={t('analysis:pick_gallery_picture')!}
                disabled={isPending || isProcessing}
                expand="block"
                style={{ flexGrow: 1 }}
                {...props}
                onClick={(event) => {
                  markPicturePickerActive();
                  props.onClick?.(event);
                }}
              >
                <IonIcon
                  aria-hidden="true"
                  icon={imageOutline}
                  slot="icon-only"
                ></IonIcon>
              </IonButton>
            )}
          </FileButton>
        </Group>
        {hasProcessError ? (
          <Text color="red">{t('analysis:picture_process_error')}</Text>
        ) : null}
      </div>
      {imageUrl ? (
        <>
          <IonButton
            size="small"
            onClick={() => {
              if (imageUrl) {
                setShowImage((prev) => !prev);
              }
            }}
          >
            Voir l'image
          </IonButton>
          <Collapse in={showImage}>
            <Image
              alt="Uploaded picture"
              height={150}
              mb="sm"
              src={root + imageUrl}
              onClick={() => setLightboxOpen(true)}
            />
            <Lightbox
              carousel={{
                finite: true,
              }}
              close={() => setLightboxOpen(false)}
              controller={{ closeOnPullDown: true, closeOnBackdropClick: true }}
              open={lightboxOpen}
              plugins={[Zoom]}
              render={{
                buttonPrev: () => null,
                buttonNext: () => null,
              }}
              slides={[
                {
                  type: 'image',
                  src: root + imageUrl,
                  alt: 'Uploaded picture',
                },
              ]}
            />
          </Collapse>
        </>
      ) : null}
    </Stack>
  );
};
